import React, { useMemo, useState } from 'react';
import { Phone, List, Calendar as CalendarIcon, Mail } from 'lucide-react';
import { mockClasses, Format, CECategory, ClassRecord } from '../data/classes';
import { FindAClassFilters } from '../components/classes/FindAClassFilters';
import { ClassCalendar } from '../components/classes/ClassCalendar';
import { ClassList } from '../components/classes/ClassList';
import { ClassDetailDrawer } from '../components/classes/ClassDetailDrawer';
import { DayClassesDrawer } from '../components/classes/DayClassesDrawer';
import { SalespersonRequirementTable, BrokerRequirementTable } from '../components/RequirementTables';
export function LiveClasses() {
  // Filter State
  const [formatFilter, setFormatFilter] = useState<Format | 'All Formats'>('All Formats');
  const [categoryFilter, setCategoryFilter] = useState<CECategory | 'All Categories'>('All Categories');
  const [searchQuery, setSearchQuery] = useState('');
  // View State
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 5, 1)); // June 2026
  // Drawer State
  const [selectedClass, setSelectedClass] = useState<ClassRecord | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [selectedDayDate, setSelectedDayDate] = useState<string | null>(null);
  const [selectedDayClasses, setSelectedDayClasses] = useState<ClassRecord[]>([]);
  const [isDayDrawerOpen, setIsDayDrawerOpen] = useState(false);
  // Filter Logic
  const filteredClasses = useMemo(() => {
    return mockClasses.filter(cls => {
      // Hide past classes (assuming today is June 5, 2026)
      const classDate = new Date(cls.date + 'T00:00:00');
      const today = new Date('2026-06-05T00:00:00');
      if (classDate < today) return false;
      // Format filter
      if (formatFilter !== 'All Formats' && cls.format !== formatFilter) return false;
      // Category filter
      if (categoryFilter !== 'All Categories' && cls.ceCategory !== categoryFilter) return false;
      // Search query
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        const matchesTitle = cls.title.toLowerCase().includes(query);
        const matchesInstructor = cls.instructorName.toLowerCase().includes(query);
        const matchesLocation = cls.location.toLowerCase().includes(query);
        const matchesDesc = cls.description.toLowerCase().includes(query);
        if (!matchesTitle && !matchesInstructor && !matchesLocation && !matchesDesc) return false;
      }
      return true;
    }).sort((a, b) => {
      // Sort by date soonest first
      const dateA = new Date(a.date + 'T' + (a.startTime.includes('AM') ? a.startTime.replace(' AM', ':00') : a.startTime.replace(' PM', ':00')));
      const dateB = new Date(b.date + 'T' + (b.startTime.includes('AM') ? b.startTime.replace(' AM', ':00') : b.startTime.replace(' PM', ':00')));
      return dateA.getTime() - dateB.getTime();
    });
  }, [formatFilter, categoryFilter, searchQuery]);
  const handleResetFilters = () => {
    setFormatFilter('All Formats');
    setCategoryFilter('All Categories');
    setSearchQuery('');
  };
  const handleShowNext = () => {
    // Clear filters so the soonest upcoming classes surface, switch to list, and scroll to results
    setFormatFilter('All Formats');
    setCategoryFilter('All Categories');
    setSearchQuery('');
    setViewMode('list');
    const results = document.getElementById('class-results');
    if (results) {
      results.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const handleClassClick = (classRecord: ClassRecord) => {
    setSelectedClass(classRecord);
    setIsDetailDrawerOpen(true);
  };
  const handleDayClick = (date: string, dayClasses: ClassRecord[]) => {
    setSelectedDayDate(date);
    setSelectedDayClasses(dayClasses);
    setIsDayDrawerOpen(true);
  };
  return <div className="min-h-screen bg-white pt-40 lg:pt-52 pb-20" data-id="element-1179" data-mp="gcqtyse4wg1g">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1180" data-mp="3or7sf8ed3bvb">
        {/* Page Header */}
        <div className="mb-12 text-center" data-id="element-1181" data-mp="3kpw210nnymm2">
          <div className="flex items-center justify-center mb-4" data-id="element-1182" data-mp="3kr72aqhwhc8f">
            <h1 className="text-4xl font-bold text-gray-900" data-id="element-1183" data-mp="2cg15jt9krdn">
              In-Person & Zoom Arizona Real Estate CE Classes
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1184" data-mp="3tp9bhtbsfb6s">
            Find upcoming ADRE-approved Arizona real estate continuing education
            classes by date, class format, or CE category.
          </p>
        </div>

        {/* Find A Class Section */}
        <div id="find-a-class" className="scroll-mt-40" data-id="element-1185" data-mp="1git4h53xzg11">
          <FindAClassFilters formatFilter={formatFilter} setFormatFilter={setFormatFilter} categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter} searchQuery={searchQuery} setSearchQuery={setSearchQuery} onReset={handleResetFilters} onShowNext={handleShowNext} resultCount={filteredClasses.length} data-id="element-1186" data-mp="1lakqr3g2ezld" />

          {/* Free Classes Notice (near results) */}
          <div className="mb-6 bg-green-50 border-l-4 border-green-400 px-4 py-3 rounded-md" data-id="element-1187" data-mp="247411w2lutji">
            <p className="text-base font-semibold text-green-800 text-center" data-id="element-1188" data-mp="1qc1mkkjozvv3">
              Classes are FREE unless indicated otherwise
            </p>
            <p className="mt-2 text-sm text-green-800 text-center break-words" data-id="element-1189" data-mp="etgzby74bw2o">
              Phoenix Realtors-sponsored classes display as: “FREE to Phoenix
              Realtors Members, Non-members $20 (thru Phoenix Portal).”
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex justify-end mb-6" data-id="element-1190" data-mp="3rv1fbqioktce">
            <div className="inline-flex bg-gray-100 rounded-lg p-1" data-id="element-1191" data-mp="3rxnizf2gaopc">
              <button onClick={() => setViewMode('list')} className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white text-brand-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1192" data-mp="3n303u6tc76nf">
                <List className="w-4 h-4 mr-2" data-id="element-1193" data-mp="385r00dmukaet" />
                Upcoming Classes
              </button>
              <button onClick={() => setViewMode('calendar')} className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'calendar' ? 'bg-white text-brand-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1194" data-mp="345yygkvvf4iy">
                <CalendarIcon className="w-4 h-4 mr-2" data-id="element-1195" data-mp="xd67c0fzok2x" />
                Monthly Calendar
              </button>
            </div>
          </div>

          {/* Main Content Area */}
          <div id="class-results" className="mb-16 scroll-mt-40" data-id="element-1196" data-mp="19wa9k2rtzifo">
            {viewMode === 'calendar' ? <div className="hidden md:block" data-id="element-1197" data-mp="1epd2c8osanut">
                <ClassCalendar classes={filteredClasses} currentMonth={currentMonth} onMonthChange={setCurrentMonth} onClassClick={handleClassClick} onDayClick={handleDayClick} data-id="element-1198" data-mp="1jo6b5nt4lqon" />
              </div> : null}

            {/* Always show list on mobile if calendar is selected, or if list is selected */}
            <div className={viewMode === 'calendar' ? 'md:hidden' : 'block'} data-id="element-1199" data-mp="100qhxskav5wq">
              <ClassList classes={filteredClasses} onClassClick={handleClassClick} data-id="element-1200" data-mp="27ds9k91oc1vl" />
            </div>
          </div>
        </div>

        {/* CE Requirements Section (Moved from top) */}
        <div className="mb-16 border-t border-gray-200 pt-16" data-id="element-1201" data-mp="2xgw27zxa3pcp">
          <div className="text-center mb-10" data-id="element-1202" data-mp="2sht6x773oxhx">
            <h2 className="text-3xl font-bold text-gray-900" data-id="element-1203" data-mp="2sj4bvaca8bam">
              CE Requirements
            </h2>
            <p className="text-gray-600 mt-2" data-id="element-1204" data-mp="378aaaw3apxhg">
              Review your ADRE renewal requirements below.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start" data-id="element-1205" data-mp="3g6cflnu2bcaj">
            <SalespersonRequirementTable data-id="element-1206" data-mp="3g8oyq4bk3sp6" />
            <BrokerRequirementTable data-id="element-1207" data-mp="3b8khud6qkvyf" />
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mt-4" data-id="element-1208" data-mp="3ba58hrpxjur0">
            <p className="text-base text-gray-800 font-medium" data-id="element-1209" data-mp="3q2z18eu7q7dm">
              <strong className="font-bold" data-id="element-1210" data-mp="3l7086dxxw16v">ELECTIVE:</strong> For your
              remaining 3 hours you may choose any credit type, providing you do
              not duplicate any course title that you have already taken within
              the renewal period. Different class name = Different course
              number.
            </p>
          </div>
        </div>

        {/* Combined Questions / Contact CTA */}
        <div className="mt-12 bg-brand-purple-50 rounded-lg p-8 text-center border border-brand-purple-100" data-id="element-1211" data-mp="b8ge6jef4q40">
          <h2 className="text-2xl font-bold text-brand-purple-900 mb-4" data-id="element-1212" data-mp="3sndigfiv355w">
            QUESTIONS?
          </h2>
          <p className="text-gray-700 mb-6" data-id="element-1213" data-mp="1gtx46fefwtc">
            Have questions about upcoming classes or need help registering?
            We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1214" data-mp="3nry6xu8btlcn">
            <a href="tel:602-377-6207" className="inline-flex items-center justify-center px-8 py-3 bg-brand-purple-600 text-white font-bold rounded-md hover:bg-brand-purple-700 transition-colors shadow-md" data-id="element-1215" data-mp="39rf36zaw91ga">
              <Phone className="w-5 h-5 mr-2" data-id="element-1216" data-mp="17x62oskjwgyy" />
              Call or Text / 602-377-6207
            </a>
            <a href="/Contact" className="inline-flex items-center justify-center px-8 py-3 bg-white text-brand-purple-600 font-bold rounded-md border-2 border-brand-purple-600 hover:bg-brand-purple-50 transition-colors shadow-sm" data-id="element-1217" data-mp="1cvzd2c4jjruw">
              <Mail className="w-5 h-5 mr-2" data-id="element-1218" data-mp="368i70p6xskeh" />
              Contact Us / Email or Chat
            </a>
          </div>
        </div>
      </div>

      {/* Drawers */}
      <ClassDetailDrawer classRecord={selectedClass} isOpen={isDetailDrawerOpen} onClose={() => setIsDetailDrawerOpen(false)} data-id="element-1219" data-mp="2pys0od9rb1m" />

      <DayClassesDrawer date={selectedDayDate} classes={selectedDayClasses} isOpen={isDayDrawerOpen} onClose={() => setIsDayDrawerOpen(false)} onClassClick={handleClassClick} data-id="element-1220" data-mp="6pha7cdph80r" />
    </div>;
}